import { CircleDotIcon, GiftIcon, StoreCreditIcon } from "@xola/icons";
import { Table, Tooltip } from "@xola/ui-kit";
import { DateFormat, formatDate, getGiftPurchaserAndRecipient, isGiftPurchaseItem } from "common";
import React from "react";
import { useHistory } from "react-router-dom";
import { GiftPurchaseItem, LineItemObject, LineItemType, Purchase, PurchaseItemObject, PurchaseStatus } from "types";
import { useSeller } from "../../../hooks/useAccount";
import { useInfiniteCollection } from "../../../hooks/useInfiniteCollection";
import { CreatedAtRangeProps } from "../../../stores/reports/analyticsFilterStore";
import { GiftStoreProductType, useGiftFilterStore } from "../../../stores/reports/giftsReportFilterStore";
import { Currency } from "../../Common/Currency";
import { InfiniteList } from "../../Common/InfiniteList";
import { NoTranslate } from "../../Common/NoTranslate";
import { PaymentMethodIcon } from "../../Common/PaymentMethodIcon";
import { CreditCardIcon } from "@/components/Common/CreditCardIcon";
import { getPurchaseSearchTerm } from "@/helpers/purchaseSearchHelper";

const getParams = (dateParams: CreatedAtRangeProps, type: GiftStoreProductType, seller: string, search: string) => {
    const params: any = {
        ...dateParams,
        "status[notEqual]": PurchaseStatus.DRAFT,
        seller,
    };

    const searchTerm = getPurchaseSearchTerm(search);
    if (searchTerm) {
        params.search = searchTerm;
    }

    if (type === GiftStoreProductType.ALL) {
        params["items.object[in]"] = `${PurchaseItemObject.GIFT},${PurchaseItemObject.STORE_CREDIT}`;
    } else if (type === GiftStoreProductType.STORE_CREDIT) {
        params["items.object"] = PurchaseItemObject.STORE_CREDIT;
    } else {
        params["items.object"] = PurchaseItemObject.GIFT;
    }

    return params;
};

export const GiftReportTable = ({ search }: { search: string }) => {
    const seller = useSeller();
    const history = useHistory();

    const type = useGiftFilterStore((state) => state.type);
    const dateParams = useGiftFilterStore((state) => state.getDateParams());

    const {
        data: purchases,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isLoading,
    } = useInfiniteCollection<Purchase>(["purchases"], getParams(dateParams, type, seller.id, search), {
        staleTime: 100,
    });

    const handleTableRowItemClick = (purchase: Purchase) => {
        history.push(`/purchases/${purchase.id}`);
    };

    return (
        <div className="flex h-full w-full flex-col px-6 py-3">
            <InfiniteList isLoading={isLoading || isFetchingNextPage} hasMore={hasNextPage} onLoadMore={fetchNextPage}>
                <Table>
                    <Table.Head>
                        <Table.Row>
                            <Table.Header>Name</Table.Header>
                            <Table.Header>Code</Table.Header>
                            <Table.Header>Value</Table.Header>
                            <Table.Header>Amount Redeemed</Table.Header>
                            <Table.Header>Outstanding Balance</Table.Header>
                            <Table.Header>Recognized</Table.Header>
                            <Table.Header>Deferred</Table.Header>
                            <Table.Header>Issue Date</Table.Header>
                        </Table.Row>
                    </Table.Head>
                    <Table.Body>
                        {purchases.map((purchase) => {
                            return purchase.items.map((purchaseItem) => {
                                if (
                                    [PurchaseItemObject.GIFT, PurchaseItemObject.STORE_CREDIT].includes(
                                        purchaseItem.object,
                                    )
                                ) {
                                    const lineItemTypes = new Set([LineItemType.GIFT, LineItemType.ADJUSTMENT]);
                                    const nameFrom = purchase.customerName;
                                    const isGift = isGiftPurchaseItem(purchaseItem);
                                    const giftParties = getGiftPurchaserAndRecipient(purchase, purchaseItem);
                                    const { redeemed, balance, recognizedIncome, deferredIncome, recipientName } =
                                        purchaseItem as GiftPurchaseItem;
                                    const lineItems = purchaseItem.lineItems.filter(
                                        (lineItem) => lineItemTypes.has(lineItem.type) && !!lineItem.includeInAmount,
                                    );
                                    const code = purchaseItem.couponCode?.code ?? "--";
                                    const amount = Object.values(lineItems).reduce(
                                        (total, { amount }) => total + amount,
                                        0,
                                    );
                                    const payment = purchase.lineItems.find(
                                        (item) => item.object === LineItemObject.PAYMENT,
                                    )?.payment;

                                    return (
                                        <Table.Row
                                            key={purchaseItem.id}
                                            className="cursor-pointer border-b border-gray-lighter"
                                            onClick={() => handleTableRowItemClick(purchase)}
                                        >
                                            <Table.Cell className="py-2">
                                                <div className="flex items-center space-x-2">
                                                    {isGiftPurchaseItem(purchaseItem) ? (
                                                        <Tooltip content="Gift">
                                                            <GiftIcon />
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip content="Store Credit">
                                                            <StoreCreditIcon />
                                                        </Tooltip>
                                                    )}
                                                    <div>
                                                        <div data-testid="gift-report-table-name-from">
                                                            <span className="mr-1 font-bold">From:</span>
                                                            <GiftPartyName
                                                                name={isGift ? giftParties.from : nameFrom}
                                                                isGift={isGift}
                                                            />
                                                        </div>
                                                        <div data-testid="gift-report-table-name-to">
                                                            <span className="mr-1 font-bold">To:</span>
                                                            <GiftPartyName
                                                                name={isGift ? giftParties.to : recipientName ?? "--"}
                                                                isGift={isGift}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell className="no-translate font-bold">{code}</Table.Cell>
                                            <Table.Cell>
                                                <Currency>{amount}</Currency>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Currency>{redeemed}</Currency>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Currency>{balance}</Currency>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Currency>{recognizedIncome}</Currency>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Currency>{deferredIncome}</Currency>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center space-x-2">
                                                    <div>
                                                        <div>
                                                            {payment?.method ? (
                                                                <PaymentMethodIcon method={payment.method} />
                                                            ) : (
                                                                <CircleDotIcon />
                                                            )}
                                                            <NoTranslate className="ml-1">
                                                                {formatDate(purchase.createdAt, DateFormat.DATE_SHORT)}
                                                            </NoTranslate>
                                                        </div>
                                                        <div>
                                                            {payment?.meta?.cardSummary && (
                                                                <span className="ml-1">
                                                                    (
                                                                    <CreditCardIcon
                                                                        name={payment.meta.cardSummary.brand}
                                                                    />
                                                                    <NoTranslate className="ml-2">
                                                                        **{payment.meta.cardSummary.last4})
                                                                    </NoTranslate>
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    );
                                }

                                return null;
                            });
                        })}
                    </Table.Body>
                </Table>
            </InfiniteList>
        </div>
    );
};

interface GiftPartyNameProps {
    name?: string;
    isGift: boolean;
}

// Gift organizer and recipient details are optional, so a missing side is labelled rather than left blank.
const GiftPartyName = ({ name, isGift }: GiftPartyNameProps) => {
    if (!name && isGift) {
        return <i className="text-gray-dark">Not collected</i>;
    }

    return <NoTranslate>{name}</NoTranslate>;
};
